import { useEffect, useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { api, ApiHata } from "@/lib/api";
import { Logo } from "@/components/logo";
import { girdi } from "./Giris";
import { cevir } from "@/lib/dil";

interface JetonBilgi {
  gecerli: boolean;
  ad?: string;
  eposta?: string;
  tip?: "davet" | "sifirlama";
}

/** Maildeki bağlantının açtığı ekran; oturumsuz çalışır (davet edilenin henüz hesabı yok). */
export function ParolaBelirle({ jeton }: { jeton: string }) {
  const [bilgi, setBilgi] = useState<JetonBilgi | null>(null);
  const [yeni, setYeni] = useState("");
  const [tekrar, setTekrar] = useState("");
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [bitti, setBitti] = useState(false);

  useEffect(() => {
    api.get<JetonBilgi>(`/auth/parola-jetonu/${encodeURIComponent(jeton)}`).then(setBilgi).catch(() => setBilgi({ gecerli: false }));
  }, [jeton]);

  const zayif = yeni.length > 0 && yeni.length < 8;
  const uyusmuyor = tekrar.length > 0 && yeni !== tekrar;

  async function gonder(e: FormEvent) {
    e.preventDefault();
    setHata(null);
    setMesgul(true);
    try {
      await api.post("/auth/parola-belirle", { jeton, yeniParola: yeni });
      setBitti(true);
    } catch (err) {
      setHata(err instanceof ApiHata ? err.message : cevir("Password could not be set. Try again."));
      setMesgul(false);
    }
  }

  const kutu = "w-full max-w-sm rounded-xl border border-line bg-surface p-6 shadow-[var(--shadow-lg)]";
  const sarmal = "flex min-h-dvh items-center justify-center bg-bg p-4 font-plex";
  const buton = "mt-5 block w-full rounded-md bg-navy px-4 py-2.5 text-center font-semibold text-white disabled:opacity-40 dark:text-[#0a141c]";

  if (bilgi === null) return <main className={sarmal}><p className="text-base text-ink-3">{cevir("Loading...")}</p></main>;

  if (!bilgi.gecerli) {
    return (
      <main className={sarmal}>
        <div className={kutu}>
          <Logo height={24} />
          <h1 className="mt-3 text-lg font-semibold">{cevir("Invalid link")}</h1>
          <p className="mt-3 text-base leading-relaxed text-ink-2">{cevir("This link has expired or has already been used. Password setting links can be used")} <strong>{cevir("once")}</strong> .</p>
          <p className="mt-2 text-base leading-relaxed text-ink-2">{cevir("From the login screen")} <strong>{cevir("\"I forgot my password\"")}</strong> {cevir("you can request a new link with.")}</p>
          <Link to="/" className={buton}>{cevir("Go to login screen")}</Link>
        </div>
      </main>
    );
  }

  if (bitti) {
    return (
      <main className={sarmal}>
        <div className={kutu}>
          <Logo height={24} />
          <h1 className="mt-3 text-lg font-semibold text-ok">{cevir("Your password has been set")}</h1>
          <p className="mt-3 text-base leading-relaxed text-ink-2">{cevir("You can now log in with")} <strong>{bilgi.eposta}</strong> {cevir("and your new password.")}</p>
          <Link to="/" className={buton}>{cevir("Login")}</Link>
        </div>
      </main>
    );
  }

  const davet = bilgi.tip === "davet";
  return (
    <main className={sarmal}>
      <form onSubmit={gonder} className={kutu}>
        <Logo height={24} />
        <h1 className="mt-3 text-lg font-semibold">{davet ? cevir("Welcome") : cevir("Set new password")}</h1>
        <p className="mb-5 mt-1 text-base leading-relaxed text-ink-2">{bilgi.ad}, {davet ? cevir("Your OASIS account has been opened. ") : ""}{cevir("set a password for yourself. Your login address:")} <strong>{bilgi.eposta}</strong></p>
        <label className="mb-1 block text-base font-medium text-ink-2" htmlFor="yeni">{cevir("New password")}</label>
        <input id="yeni" className={girdi} type="password" autoComplete="new-password" autoFocus value={yeni} onChange={(e) => setYeni(e.target.value)} required />
        <p className={`mb-4 mt-1 text-sm ${zayif ? "text-bad" : "text-ink-3"}`}>{cevir("Min 8 characters.")}</p>
        <label className="mb-1 block text-base font-medium text-ink-2" htmlFor="tekrar">{cevir("New password again")}</label>
        <input id="tekrar" className={girdi} type="password" autoComplete="new-password" value={tekrar} onChange={(e) => setTekrar(e.target.value)} required />
        {uyusmuyor && <p className="mt-1 text-sm text-bad">{cevir("Passwords do not match.")}</p>}
        {hata && <p className="mt-4 rounded-md bg-bad-bg p-3 text-base text-bad">{hata}</p>}
        <button disabled={mesgul || zayif || uyusmuyor || !yeni || !tekrar} className={buton}>{mesgul ? cevir("Saving…") : cevir("Set password")}</button>
      </form>
    </main>
  );
}
